(() => {
  const SELECTOR = '[data-pack-builder]';

  class PackBuilder {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-pack-form]');
      this.components = [...root.querySelectorAll('[data-pack-component]')];
      this.progress = root.querySelector('[data-pack-progress]');
      this.source = root.dataset.packSource;
      this.variantInput = this.form?.querySelector('[data-pack-variant-id]');
      this.variantData = this.readVariantData();
      this.price = root.querySelector('.pack-product__price');
      this.submitText = this.form?.querySelector('[data-pack-submit-text]');
      this.submitButton = this.form?.querySelector('[type="submit"]');
      this.formError = root.querySelector('.pack-product__form-error');
      this.form?.addEventListener('submit', (event) => this.validate(event), true);
      root.addEventListener('click', (event) => this.choose(event));
      if (this.isNativeBundle() && this.variantInput) this.variantInput.value = '';
      this.sync();
    }

    isNativeBundle() {
      return this.source === 'native-bundle';
    }

    readVariantData() {
      if (!this.isNativeBundle()) return [];
      const element = this.root.querySelector('[data-pack-variant-data]');
      if (!element) return [];

      try {
        const variants = JSON.parse(element.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.error('Unable to read pack variant data.', error);
        return [];
      }
    }

    choose(event) {
      const choice = event.target.closest('[data-pack-choice]');
      if (!choice || choice.disabled || !this.root.contains(choice)) return;

      const component = choice.closest('[data-pack-component]');
      const group = choice.closest('[data-pack-group]');
      const input = group?.querySelector('[data-pack-input]');
      if (!component || !group || !input) return;

      group.querySelectorAll('[data-pack-choice]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button === choice));
      });
      input.value = choice.dataset.value || choice.textContent.trim();
      input.dataset.selectedValue = input.value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      component.classList.remove('has-error', 'pack-shake');
      component.querySelectorAll('[data-pack-input]').forEach((field) => field.removeAttribute('aria-invalid'));
      this.clearFormError();
      this.sync();
    }

    componentComplete(component) {
      return [...component.querySelectorAll('[data-pack-input][data-required="true"]')].every((input) => input.value);
    }

    nativeSelections() {
      return this.components.map((component) => component.querySelector('[data-pack-native-input]')?.value || '');
    }

    variantMatchesSelections(variant, selections, ignoredIndex = -1) {
      return selections.every((selection, index) => (
        index === ignoredIndex || !selection || String(variant.options[index]) === selection
      ));
    }

    refreshNativeAvailability() {
      if (!this.isNativeBundle()) return;
      const selections = this.nativeSelections();

      this.components.forEach((component, optionIndex) => {
        component.querySelectorAll('[data-pack-native-choice]').forEach((choice) => {
          const value = choice.dataset.value || choice.textContent.trim();
          const compatible = this.variantData.some((variant) => (
            variant.available
            && String(variant.options[optionIndex]) === value
            && this.variantMatchesSelections(variant, selections, optionIndex)
          ));
          choice.disabled = !compatible;
          choice.setAttribute('aria-disabled', String(!compatible));
        });
      });
    }

    resolveNativeVariant() {
      if (!this.isNativeBundle()) return null;
      const selections = this.nativeSelections();
      const complete = selections.length === this.components.length && selections.every(Boolean);
      const variant = complete
        ? this.variantData.find((candidate) => candidate.available && this.variantMatchesSelections(candidate, selections))
        : null;

      if (this.variantInput) {
        this.variantInput.value = variant?.id || '';
        this.variantInput.dataset.resolvedVariantId = variant?.id || '';
      }
      if (variant?.priceFormatted) {
        if (this.price) this.price.textContent = variant.priceFormatted;
        if (this.submitText) {
          const label = this.submitButton?.dataset.packButtonLabel || 'ADD TO CART';
          this.submitText.textContent = `${label} · ${variant.priceFormatted}`;
        }
      }
      return variant;
    }

    clearFormError() {
      if (!this.formError) return;
      this.formError.hidden = true;
      const message = this.formError.querySelector('.product-form__error-message');
      if (message) message.textContent = '';
    }

    showFormError(message) {
      if (!this.formError) return;
      const messageElement = this.formError.querySelector('.product-form__error-message');
      if (messageElement) messageElement.textContent = message;
      this.formError.hidden = false;
    }

    sync() {
      if (this.isNativeBundle()) {
        this.refreshNativeAvailability();
        this.resolveNativeVariant();
      }

      let selected = 0;
      this.components.forEach((component) => {
        const complete = this.componentComplete(component);
        const choices = [...component.querySelectorAll('[data-pack-choice]')];
        const unavailable = choices.length > 0 && choices.every((choice) => choice.disabled);
        component.classList.toggle('is-complete', complete);
        const status = component.querySelector('[data-pack-status]');
        if (status) status.textContent = unavailable ? 'Unavailable' : (complete ? 'Selected' : 'Choose size');
        if (complete) selected += 1;
      });

      const total = this.components.length;
      if (this.progress) this.progress.textContent = `${selected} of ${total} selected`;
      document.dispatchEvent(new CustomEvent('pack:selection-change', {
        detail: {
          selected,
          total,
          sectionId: this.root.dataset.sectionId,
          priceFormatted: this.price?.textContent?.trim() || ''
        }
      }));
    }

    validate(event) {
      const missing = this.components.find((component) => !this.componentComplete(component));
      if (!missing) {
        if (!this.isNativeBundle() || this.resolveNativeVariant()) {
          this.clearFormError();
          return true;
        }

        event?.preventDefault();
        event?.stopImmediatePropagation();
        this.showFormError('This size combination is unavailable. Choose another combination.');
        this.root.querySelector('.pack-builder')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if ('vibrate' in navigator) navigator.vibrate([35, 25, 35]);
        return false;
      }

      event?.preventDefault();
      event?.stopImmediatePropagation();
      missing.classList.remove('pack-shake');
      void missing.offsetWidth;
      missing.classList.add('has-error', 'pack-shake');
      this.showFormError('Select a size for every item before adding this pack to your cart.');
      missing.querySelectorAll('[data-pack-input][data-required="true"]').forEach((input) => {
        if (!input.value) input.setAttribute('aria-invalid', 'true');
      });
      missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => missing.querySelector('[data-pack-choice]:not([disabled])')?.focus({ preventScroll: true }), 420);
      if ('vibrate' in navigator) navigator.vibrate([35, 25, 35]);
      return false;
    }

    submit() {
      if (!this.form) return;
      if (this.validate()) this.form.requestSubmit();
    }
  }

  const builders = new Map();
  document.querySelectorAll(SELECTOR).forEach((root) => {
    const builder = new PackBuilder(root);
    builders.set(root.dataset.sectionId, builder);
  });

  document.querySelectorAll('[data-pack-sticky]').forEach((sticky) => {
    const targetId = sticky.dataset.targetSection;
    const progress = sticky.querySelector('[data-pack-sticky-progress]');
    const stickyPrice = sticky.querySelector('.pack-sticky__price');
    const stickyButton = sticky.querySelector('[data-pack-sticky-submit]');
    const targetBuilder = builders.get(targetId)
      || [...builders.values()].find((builder) => builder.root.dataset.sectionId?.endsWith(`__${targetId}`))
      || (builders.size === 1 ? builders.values().next().value : null);
    const resolvedTargetId = targetBuilder?.root.dataset.sectionId;

    if (progress && targetBuilder) {
      const selected = targetBuilder.components.filter((component) => targetBuilder.componentComplete(component)).length;
      progress.textContent = `${selected} of ${targetBuilder.components.length} selected`;
    }
    stickyButton?.addEventListener('click', () => targetBuilder?.submit());
    document.addEventListener('pack:selection-change', (event) => {
      if (event.detail.sectionId !== resolvedTargetId || !progress) return;
      progress.textContent = `${event.detail.selected} of ${event.detail.total} selected`;
      if (stickyPrice && event.detail.priceFormatted) stickyPrice.textContent = event.detail.priceFormatted;
    });
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-pack-size-guide-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.target);
      if (dialog?.showModal) {
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    const closeButton = event.target.closest('[data-pack-size-guide-close]');
    if (closeButton) {
      closeButton.closest('dialog')?.close();
      return;
    }

    if (event.target.matches('[data-pack-size-guide-modal]')) event.target.close();
  });

  document.querySelectorAll('[data-pack-size-guide-modal]').forEach((dialog) => {
    dialog.addEventListener('close', () => {
      const opener = document.querySelector(`[data-pack-size-guide-open][data-target="${dialog.id}"]`);
      opener?.setAttribute('aria-expanded', 'false');
      opener?.focus({ preventScroll: true });
    });
  });
})();
